/**
 * Single shared notification store.
 *
 * The header bell and the Notification Center used to run independent copies
 * of this hook, so marking a notification read on the page left the badge
 * stale until the next 30s poll. Every consumer now subscribes to one store,
 * so read/dismiss updates are reflected everywhere immediately.
 */
import { useCallback, useEffect, useSyncExternalStore } from "react";
import { useAuth } from "@/hooks/use-auth";
import { notificationService } from "@/lib/services/notificationService";
import { unreadBadgeCount } from "@/lib/notifications/model";
import type { Notification } from "@/lib/db/models";

type StoreState = {
  userId: string | null;
  items: Notification[];
  loading: boolean;
  error: string | null;
};

let state: StoreState = { userId: null, items: [], loading: true, error: null };
const listeners = new Set<() => void>();

function setState(patch: Partial<StoreState>) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function getSnapshot() {
  return state;
}

let inFlight: Promise<void> | null = null;

async function load(userId: string | null) {
  if (!userId) {
    setState({ userId: null, items: [], loading: false, error: null });
    return;
  }
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const rows = await notificationService.getMyNotifications(userId);
      setState({ userId, items: rows, loading: false, error: null });
    } catch (e) {
      setState({ loading: false, error: (e as Error).message ?? "Failed to load notifications" });
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

function patchItem(id: string, patch: Partial<Notification>) {
  setState({ items: state.items.map((n) => (n.id === id ? { ...n, ...patch } : n)) });
}

export function useNotifications() {
  const { user } = useAuth();
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    const uid = user?.id ?? null;
    if (uid !== state.userId) setState({ loading: Boolean(uid), items: [], userId: uid });
    void load(uid);
    if (!uid) return;
    const interval = setInterval(() => void load(uid), 30_000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const reload = useCallback(() => load(user?.id ?? null), [user?.id]);

  const markAsRead = useCallback(
    async (id: string) => {
      const target = state.items.find((n) => n.id === id);
      if (!target || target.read_at) return;
      patchItem(id, { read_at: new Date().toISOString() });
      try {
        await notificationService.markAsRead(id);
      } catch {
        void reload();
      }
    },
    [reload],
  );

  const markAllAsRead = useCallback(async () => {
    const uid = user?.id;
    if (!uid) return;
    const now = new Date().toISOString();
    setState({ items: state.items.map((n) => (n.read_at ? n : { ...n, read_at: now })) });
    try {
      await notificationService.markAllAsRead(uid);
    } catch {
      void reload();
    }
  }, [user?.id, reload]);

  const dismiss = useCallback(
    async (id: string) => {
      patchItem(id, { dismissed_at: new Date().toISOString() });
      try {
        await notificationService.dismiss(id);
      } catch {
        void reload();
      }
    },
    [reload],
  );

  const undismiss = useCallback(
    async (id: string) => {
      patchItem(id, { dismissed_at: null });
      try {
        await notificationService.undismiss(id);
      } catch {
        void reload();
      }
    },
    [reload],
  );

  return {
    items: snap.items,
    loading: snap.loading,
    error: snap.error,
    unreadCount: unreadBadgeCount(snap.items),
    reload,
    markAsRead,
    markAllAsRead,
    dismiss,
    undismiss,
  };
}
